import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  LookupsParams,
  Organization,
  UserQueryParams,
} from "@/modules/common/dtos/index.v2";
import { queryCommonFlags } from "@/api/constants";
import { fetchUsersLookups } from "@/modules/common/api/index.v2";
import { debounce, isEqual } from "lodash";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import { OrderDirection } from "@/api/types";
import { UsersOrderByOptions } from "@/modules/user/api/types";

interface UserSearchParams {
  initialParams?: Partial<UserQueryParams>;
  queryKey?: string;
  currentPage?: string;
}

const useUsersFilterOptions = ({
  initialParams,
  queryKey,
  currentPage,
}: UserSearchParams = {}) => {
  const { selectedOrganization, selectedOrganizations } = useOrganization();
  const [searchTerm, setSearchTerm] = useState("");

  const storedOrgName = localStorage.getItem("selectedOrganizationName");
  const listingDashboardPendingFilters = localStorage.getItem(
    `ListingsTableFiltersPending${storedOrgName}`
  );
  const parsedListingDashboardPendingFilters = listingDashboardPendingFilters
    ? JSON.parse(listingDashboardPendingFilters)
    : {};

  const [queryParams, setQueryParams] = useState<LookupsParams>(() => ({
    ...initialParams,
    page: 1,
    pageSize: 50,
    orderBy: UsersOrderByOptions.RANK,
    orderDirection: OrderDirection.DESCENDING,
  }));

  const updateParams = useCallback((newParams: Partial<LookupsParams>) => {
    setQueryParams((prev) => {
      const updated = { ...prev, ...newParams };
      return isEqual(prev, updated) ? prev : updated;
    });
  }, []);

  const handleSearchChange = useMemo(
    () =>
      debounce((newSearchTerm) => {
        setSearchTerm(newSearchTerm);
        if (newSearchTerm.trim() !== "") {
          updateParams({ name: newSearchTerm });
        } else {
          setQueryParams((prev) => {
            const { name, ...rest } = prev;
            return rest;
          });
        }
      }, 300),
    []
  );

  useEffect(() => {
    const newParams: Partial<LookupsParams> = {};

    if (initialParams?.organizationUIds) {
      newParams.organizationUIds = initialParams.organizationUIds;
    } else if (selectedOrganizations && selectedOrganizations.length > 0) {
      newParams.organizationUIds = selectedOrganizations
        .map((org: Organization) => org.uid)
        .join(",");
    } else if (selectedOrganization?.uid) {
      newParams.organizationUIds = selectedOrganization.uid;
    }

    if (
      currentPage === "ListingDashboard" &&
      parsedListingDashboardPendingFilters?.assignedAgentsUIds
    ) {
      newParams.savedUIds =
        parsedListingDashboardPendingFilters.assignedAgentsUIds;
    }

    if (initialParams?.includeOnlyExpoUsers) {
      newParams.includeOnlyExpoUsers = initialParams.includeOnlyExpoUsers;
    }

    updateParams(newParams);
  }, [
    initialParams?.organizationUIds,
    initialParams?.includeOnlyExpoUsers,
    selectedOrganization,
    selectedOrganizations,
    currentPage,
    parsedListingDashboardPendingFilters?.assignedAgentsUIds,
  ]);

  const memoizedQueryKey = useMemo(
    () => [queryKey, queryParams],
    [queryKey, queryParams]
  );

  useEffect(() => {
    if (searchTerm.trim() !== "") {
      updateParams({ name: searchTerm });
    } else {
      setQueryParams((prev) => {
        const { name, ...rest } = prev;
        return rest;
      });
    }
  }, [searchTerm]);
  const {
    data: usersFilterOptions,
    error: usersFilterOptionsError,
    refetch: refetchUsersFilterOptions,
    isLoading: usersFilterOptionsLoading,
  } = useQuery({
    queryKey: memoizedQueryKey,
    queryFn: () => fetchUsersLookups(queryParams),
    ...queryCommonFlags,
    staleTime: 60000,
    enabled: !!queryParams.organizationUIds,
  });

  return {
    usersFilterOptions: usersFilterOptions?.data.results,
    usersFilterOptionsError,
    usersFilterOptionsLoading,
    handleSearchChange,
    refetchUsersFilterOptions,
    setSearchTerm,
    searchTerm,
  };
};

export default useUsersFilterOptions;
